// Password Reset History Utility
// Tracks all password reset events with detailed information
export interface PasswordResetHistoryEntry {
  id: string
  userId: string
  userName: string
  timestamp: string
  resetBy: 'BO_ADMIN' | 'SYSTEM' | 'USER'
  resetByName?: string // Name of admin who performed reset
  reason?: string
  deliveryStatus: 'SUCCESS' | 'FAILED' | 'PENDING'
  deliveryError?: string
  emailMessageId?: string
  recipientEmail: string
  otpGenerated: boolean
  ipAddress?: string
}
const RESET_HISTORY_STORAGE_KEY = 'password_reset_history'
// Load password reset history from localStorage
const loadResetHistory = (): PasswordResetHistoryEntry[] => {
  try {
    const stored = localStorage.getItem(RESET_HISTORY_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Error loading password reset history:', error)
    return []
  }
}
// Save password reset history to localStorage
const saveResetHistory = (history: PasswordResetHistoryEntry[]): void => {
  try {
    localStorage.setItem(RESET_HISTORY_STORAGE_KEY, JSON.stringify(history))
  } catch (error) {
    console.error('Error saving password reset history:', error)
  }
}
// In-memory storage for reset history (synced with localStorage)
let resetHistory: PasswordResetHistoryEntry[] = loadResetHistory()
// Generate unique ID for reset history entries
const generateId = (): string => {
  return `reset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
// Create password reset history entry
export const createPasswordResetHistory = (
  userId: string,
  userName: string,
  resetBy: 'BO_ADMIN' | 'SYSTEM' | 'USER',
  recipientEmail: string,
  deliveryStatus: 'SUCCESS' | 'FAILED' | 'PENDING',
  options?: {
    resetByName?: string
    reason?: string
    deliveryError?: string
    emailMessageId?: string
    ipAddress?: string
  },
): PasswordResetHistoryEntry => {
  const entry: PasswordResetHistoryEntry = {
    id: generateId(),
    userId,
    userName,
    timestamp: new Date().toISOString(),
    resetBy,
    resetByName: options?.resetByName || 'Admin User',
    reason: options?.reason,
    deliveryStatus,
    deliveryError: options?.deliveryError,
    emailMessageId: options?.emailMessageId,
    recipientEmail,
    otpGenerated: true,
    ipAddress: options?.ipAddress,
  }
  resetHistory.push(entry)
  saveResetHistory(resetHistory)
  console.log('Password reset history created:', entry)
  return entry
}
// Log email resend event
export const logEmailResend = (
  userId: string,
  userName: string,
  recipientEmail: string,
  success: boolean,
  reason?: string,
): void => {
  createPasswordResetHistory(
    userId,
    userName,
    'BO_ADMIN',
    recipientEmail,
    success ? 'SUCCESS' : 'FAILED',
    {
      resetByName: 'Admin User',
      reason: reason || 'Email resend requested by admin',
      deliveryError: success ? undefined : 'Email delivery failed',
    },
  )
  console.log('Email resend logged:', {
    userId,
    userName,
    recipientEmail,
    success,
    timestamp: new Date().toISOString(),
  })
}
// Get password reset history for a specific user
export const getPasswordResetHistoryByUserId = (
  userId: string,
): PasswordResetHistoryEntry[] => {
  return resetHistory
    .filter((entry) => entry.userId === userId)
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
}
// Get all password reset history
export const getAllPasswordResetHistory = (): PasswordResetHistoryEntry[] => {
  return resetHistory.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )
}
// Get password reset statistics
export const getPasswordResetStats = () => {
  const stats = {
    total: resetHistory.length,
    byResetBy: {
      BO_ADMIN: resetHistory.filter((entry) => entry.resetBy === 'BO_ADMIN')
        .length,
      SYSTEM: resetHistory.filter((entry) => entry.resetBy === 'SYSTEM').length,
      USER: resetHistory.filter((entry) => entry.resetBy === 'USER').length,
    },
    byDeliveryStatus: {
      SUCCESS: resetHistory.filter(
        (entry) => entry.deliveryStatus === 'SUCCESS',
      ).length,
      FAILED: resetHistory.filter((entry) => entry.deliveryStatus === 'FAILED')
        .length,
      PENDING: resetHistory.filter(
        (entry) => entry.deliveryStatus === 'PENDING',
      ).length,
    },
    successRate:
      resetHistory.length > 0
        ? (
            (resetHistory.filter((entry) => entry.deliveryStatus === 'SUCCESS')
              .length /
              resetHistory.length) *
            100
          ).toFixed(2)
        : '0',
  }
  return stats
}
// Clear all password reset history (for testing purposes)
export const clearPasswordResetHistory = (): void => {
  resetHistory = []
  saveResetHistory(resetHistory)
}
// Get the last password reset history entry for a user
export const getLastPasswordResetForUser = (
  userId: string,
): PasswordResetHistoryEntry | null => {
  const userHistory = getPasswordResetHistoryByUserId(userId)
  return userHistory.length > 0 ? userHistory[0] : null
}
// Check if user's last email delivery failed
export const hasLastEmailFailed = (userId: string): boolean => {
  const lastReset = getLastPasswordResetForUser(userId)
  return lastReset ? lastReset.deliveryStatus === 'FAILED' : false
}
// Check if user should see resend email option
export const shouldShowResendEmail = (user: any): boolean => {
  // Show if user has not logged in yet
  if (user.firstLoginRequired || user.passwordResetRequired) {
    return true
  }
  // Show if last email delivery failed
  if (hasLastEmailFailed(user.id.toString())) {
    return true
  }
  return false
}
